import React from 'react';
import Dialog from './Dialog';
import ModalActions from './ModalActions';
import { Spinner } from './AsyncState';
import './FilePreCheckModal.css';

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// 서버 메시지 앞에 붙어 오는 경고 이모지를 걷어낸다 — 상태는 아이콘·색이 이미 말해준다.
export const normalizeText = (text = '') =>
  String(text).replace(/^⚠️\s*/u, '').trim();

const truncateName = (name = '', max = 34) => {
  if (!name) return '첨부 파일';
  if (name.length <= max) return name;
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot) : '';
  const base = dot > 0 ? name.slice(0, dot) : name;
  const keep = Math.max(6, max - ext.length - 1);
  return `${base.slice(0, keep)}…${ext}`;
};

/**
 * 파일 사전 검사 모달 (Dialog 프리미티브 위)
 * - 검사 중: 닫을 수 없는 작은 다이얼로그 + 2px 스피너 (가짜 진행률 없음, 그룹 수는 실제 값만 표시)
 * - 결과: 요약 카드 → 파일별 이슈 → 자세히 보기(권장사항·전체 파일) → 액션 (primary 1개)
 */
const FilePreCheckModal = ({
  isOpen,
  onClose,
  onConfirm,
  checkResult,
  isLoading = false,
  loadingProgress = null,
  alternateAction = null,
}) => {
  if (!isOpen) return null;

  if (isLoading) {
    const showChunkProgress =
      loadingProgress &&
      typeof loadingProgress.current === 'number' &&
      typeof loadingProgress.total === 'number' &&
      loadingProgress.total > 1;

    return (
      <Dialog
        align="center"
        description="분석 가능한 분량인지 빠르게 확인 중이에요"
        dismissible={false}
        icon={<Spinner size={32} thickness={2} label="파일을 확인하는 중" />}
        open
        panelClassName="fpc-modal fpc-modal-loading"
        size="sm"
        testId="fpc-loading"
        title="파일을 살펴보고 있어요"
      >
        {showChunkProgress ? (
          <p className="fpc-loading-chunk">
            {loadingProgress.current}/{loadingProgress.total} 그룹 확인 중
          </p>
        ) : null}
      </Dialog>
    );
  }

  if (!checkResult) return null;

  const {
    can_proceed,
    overall_status,
    title: customTitle,
    message: customMessage,
    status_label,
    summary_label,
    confirm_label,
    file_badge_label,
    files_info = [],
    total_files,
    total_size_display,
    recommendations = [],
    file_count_error,
    combined_warning,
  } = checkResult;

  const isError = overall_status === 'error';
  const tone = isError ? 'error' : 'warning';
  const filesWithIssues = files_info.filter(file =>
    file?.issues?.length > 0 || file?.status === 'error' || file?.status === 'warning'
  );
  const mainIssue =
    file_count_error ||
    combined_warning ||
    filesWithIssues[0]?.issues?.[0] ||
    recommendations[0] ||
    '';

  const statusLabel = status_label || (isError ? '다시 선택 필요' : '일부만 분석');
  const title = customTitle || (isError ? '파일을 다시 골라주세요' : '일부만 분석될 수 있어요');
  const subtitle = customMessage || (isError
    ? 'AI가 읽기 어려운 조건이 있어요. 아래 내용을 고치면 바로 다시 올릴 수 있어요.'
    : '초과한 부분은 빼고, 읽을 수 있는 내용부터 분석할게요.');
  const summaryItems = [
    { label: '파일', value: `${total_files || files_info.length || 0}개` },
    { label: '용량', value: total_size_display || '-' },
    { label: '확인', value: statusLabel },
  ];

  let actions;
  if (can_proceed) {
    actions = {
      primary: { label: confirm_label || '이대로 올리기', onClick: onConfirm },
      secondary: { label: '취소', onClick: onClose },
    };
  } else if (alternateAction) {
    actions = {
      primary: { label: alternateAction.label, onClick: alternateAction.onClick },
      secondary: { label: '다시 선택하기', onClick: onClose },
    };
  } else {
    actions = {
      primary: { label: '다시 선택하기', onClick: onClose },
    };
  }

  return (
    <Dialog
      description={subtitle}
      eyebrow="파일 확인"
      footer={<ModalActions {...actions} testId="fpc-actions" />}
      onClose={onClose}
      open
      panelClassName={`fpc-modal fpc-modal-${tone}`}
      size="md"
      testId="fpc-modal"
      title={title}
    >
      <div className="fpc-body">
        <div className="fpc-summary-card">
          <div className={`fpc-status-icon ${tone}`}>
            {isError ? <ErrorIcon /> : <WarningIcon />}
          </div>
          <div className="fpc-summary-copy">
            <span className="fpc-summary-label">
              {summary_label || (isError ? '지금은 올릴 수 없어요' : '이대로 올릴 수 있어요')}
            </span>
            <strong>
              {normalizeText(mainIssue) || (isError ? '파일 조건을 다시 확인해주세요' : '분량이 많은 파일은 일부만 분석돼요')}
            </strong>
          </div>
        </div>

        <div className="fpc-summary-grid">
          {summaryItems.map(item => (
            <div className="fpc-summary-item" key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>

        {filesWithIssues.length > 0 && (
          <div className="fpc-files">
            {filesWithIssues.slice(0, 4).map((file, index) => (
              <div
                className={`fpc-file ${file.status !== 'ok' ? `fpc-file-${file.status || 'warning'}` : ''}`}
                key={`${file.filename}-${index}`}
              >
                <div className="fpc-file-row">
                  <span className="fpc-file-name" title={file.filename}>
                    {truncateName(file.filename)}
                  </span>
                  <span className={`fpc-file-badge ${file.status || 'warning'}`}>
                    {file_badge_label || (file.status === 'error' ? '확인 필요' : '일부만')}
                  </span>
                </div>
                <div className="fpc-issues">
                  {(file.issues || []).slice(0, 2).map((issue, i) => (
                    <p key={i} className="fpc-issue">{normalizeText(issue)}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {(recommendations.length > 0 || files_info.length > filesWithIssues.length) && (
          <details className="fpc-details">
            <summary>자세히 보기</summary>
            {recommendations.length > 0 && (
              <div className="fpc-recommendations">
                {recommendations.slice(0, 4).map((rec, i) => (
                  <p key={i} className="fpc-recommendation">
                    <CheckIcon />
                    <span>{normalizeText(rec)}</span>
                  </p>
                ))}
              </div>
            )}
            {files_info.length > 0 && (
              <div className="fpc-all-files">
                {files_info.map((file, index) => (
                  <div key={`${file.filename}-${index}`} className="fpc-all-file">
                    <span title={file.filename}>{truncateName(file.filename, 38)}</span>
                    <small>{file.file_size_display}</small>
                  </div>
                ))}
              </div>
            )}
          </details>
        )}
      </div>
    </Dialog>
  );
};

export default FilePreCheckModal;
